import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import { hasAdminRights } from '../utils/roleUtils';
import ProfilePicture from './shared/ProfilePicture';
import AddGuestModal from './AddGuestModal';
import ParticipantsModal from './ParticipantsModal';
import DeleteEventModal from './DeleteEventModal';
import {
  XMarkIcon,
  TrashIcon,
  UserGroupIcon,
  PencilIcon,
  ClockIcon,
  UserPlusIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  CalendarIcon,
  UserIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';
import ConfirmationModal from './ConfirmationModal';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

interface EventModalProps {
  event: any;
  events: any[];
  onClose: () => void;
  onDelete: (response: any) => void;
  onBook: (id: number, count: number) => void;
  refreshCalendar: () => void;
  onEventSelect: (event: any) => void;
  onEdit?: (event: any) => void;
}

interface Issues {
  insurance: boolean;
  vaccination: boolean;
  paid: boolean;
}

interface BookingDetails {
  gebucht_am: string | null;
  gebucht_von_name: string | null;
}

const EventModal: React.FC<EventModalProps> = ({
  event: initialEvent,
  events = [],
  onClose,
  onDelete,
  onBook,
  refreshCalendar,
  onEventSelect,
  onEdit
}) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [event, setEvent] = useState(initialEvent);
  const [isBooked, setIsBooked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [participants, setParticipants] = useState<any[]>([]);
  const [, setIssues] = useState<Issues>({ insurance: true, vaccination: true, paid: true });
  const [myIssues, setMyIssues] = useState<string[]>([]);
  const [bookingDetails, setBookingDetails] = useState<BookingDetails>({ gebucht_am: null, gebucht_von_name: null });
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: 'info' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'single' as 'single' | 'two',
    onConfirm: () => {}
  });
  const [showEventList, setShowEventList] = useState(!event && events.length > 1);
  const [isVisible, setIsVisible] = useState(false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    setShowEventList(!event && events.length > 1);
  }, [event, events.length]);

  useEffect(() => {
    if (event || events.length > 0) {
      setIsVisible(true);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    return () => {
      setIsVisible(false);
      document.body.style.overflow = '';
    };
  }, [event, events.length]);

  const isBoardMember = hasAdminRights(user);
  const isTrainerUser = user?.trainer === 1;
  const isCreator = event?.extendedProps?.creator?.id === user?.mg_nr;
  const canManageEvent = isBoardMember || isTrainerUser || isCreator;
  const isPastEvent = event?.start && new Date(event.start) < new Date();
  const buchenBis = event?.extendedProps?.buchen_bis;
  const isBuchungsfristAbgelaufen = buchenBis ? new Date().toISOString().split('T')[0] > new Date(buchenBis).toISOString().split('T')[0] : false;
  const canBookDespiteDeadline = isBoardMember || isCreator;
  const isVeranstaltung = event?.extendedProps?.isVeranstaltung || false;

  // Booking status
  const bookingCount = event?.extendedProps?.bookingCount || 0;
  const maxBookable = event?.extendedProps?.bookable || 0;
  const isFullyBooked = bookingCount >= maxBookable && maxBookable > 0;
  const freeSlots = Math.max(0, maxBookable - bookingCount);

  // Determine modal variant/accent color
  const getAccentColor = () => {
    if (isBooked) return '#059669'; // success-600 - bereits gebucht
    if (isFullyBooked) return '#dc2626'; // red-600 - ausgebucht
    return '#16a34a'; // primary-600 - verfuegbar
  };

  const getModalTitle = () => {
    if (isBooked) return 'Deine Buchung';
    if (isBuchungsfristAbgelaufen && !canBookDespiteDeadline) return 'Termindetails';
    if (isFullyBooked) return 'Termindetails';
    return 'Termin buchen';
  };

  const getModalSubtitle = () => {
    if (isBooked) return 'Erfolgreich gebucht';
    if (isBuchungsfristAbgelaufen && !canBookDespiteDeadline) return 'Anmeldefrist abgelaufen';
    if (isFullyBooked) return 'Leider ausgebucht';
    return 'Buchung bestaetigen';
  };

  const checkBookingStatus = useCallback(async () => {
    if (!event?.id) return;

    try {
      const response = await apiClient.get(`/api/members/check-event-booking/${event.id}`);
      if (response.data.success) {
        setIsBooked(response.data.isBooked);
        setIssues(response.data.issues || { insurance: true, vaccination: true, paid: true });
        const issuesArray = response.data.myIssues || [];
        const stringIssues = issuesArray
          .map((issue: any) => {
            if (typeof issue === 'string') return issue;
            if (issue && typeof issue.message === 'string') return issue.message;
            if (issue && typeof issue.toString === 'function') return issue.toString();
            return '';
          })
          .filter((issue: string) => issue.length > 0);
        setMyIssues(stringIssues);
        setBookingDetails({
          gebucht_am: response.data.gebucht_am,
          gebucht_von_name: response.data.gebucht_von_name,
        });
      }
    } catch (error) {
      console.error('Error checking booking status:', error);
    }
  }, [event?.id]);

  const fetchParticipants = useCallback(async () => {
    if (!canManageEvent || !event?.id) return;

    try {
      const response = await apiClient.get(`/api/calendar/${event.id}/participants`);
      if (response.data.success) {
        setParticipants(response.data.participants || []);
      }
    } catch (error) {
      console.error('Error fetching participants:', error);
    }
  }, [canManageEvent, event?.id]);

  useEffect(() => {
    if (event?.id) {
      checkBookingStatus();
      fetchParticipants();
    }
  }, [event, checkBookingStatus, fetchParticipants]);

  const handleMainBook = async () => {
    if (isBooked) {
      setConfirmModal({
        isOpen: true,
        type: 'warning',
        title: 'Buchung stornieren?',
        message: 'Moechten Sie Ihre Buchung fuer diesen Termin wirklich stornieren?',
        variant: 'two',
        onConfirm: performCancel
      });
    } else {
      if (myIssues && myIssues.length > 0) {
        const issuesText = myIssues.map(issue => `• ${issue}`).join('\n');
        setConfirmModal({
          isOpen: true,
          type: 'warning',
          title: 'Wichtige Hinweise',
          message: `Sie haben folgende offene Punkte:\n${issuesText}\n\nMoechten Sie trotzdem buchen?`,
          variant: 'two',
          onConfirm: performBooking
        });
        return;
      }

      if (isPastEvent) {
        setConfirmModal({
          isOpen: true,
          type: 'question',
          title: 'Vergangenen Termin buchen?',
          message: 'Dieser Termin liegt in der Vergangenheit. Moechten Sie ihn trotzdem buchen?',
          variant: 'two',
          onConfirm: performBooking
        });
        return;
      }

      await performBooking();
    }
  };

  const performBooking = async () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false }));
    setLoading(true);

    try {
      const response = await apiClient.post('/api/members/book-event', {
        kalender_id: event.id
      });

      if (response.data.success) {
        setIsBooked(true);
        onBook(event.id, (event.extendedProps?.bookingCount || 0) + 1);
        checkBookingStatus();
        refreshCalendar();

        setConfirmModal({
          isOpen: true,
          type: 'success',
          title: 'Erfolgreich gebucht',
          message: 'Ihr Platz wurde erfolgreich reserviert.',
          variant: 'single',
          onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
        });
      }
    } catch (error: any) {
      console.error('Error booking event:', error);
      let errorMessage = error.response?.data?.message || 'Die Buchung konnte nicht durchgefuehrt werden.';
      if (errorMessage.includes('Buchung fehlgeschlagen:')) {
        errorMessage = errorMessage.replace('Buchung fehlgeschlagen: ', '');
      }

      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Buchung fehlgeschlagen',
        message: errorMessage,
        variant: 'single',
        onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
      });
    } finally {
      setLoading(false);
    }
  };

  const performCancel = async () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false }));
    setLoading(true);

    try {
      const response = await apiClient.delete(`/api/members/cancel-event/${event.id}`);

      if (response.data.success) {
        setIsBooked(false);
        onBook(event.id, Math.max(0, (event.extendedProps?.bookingCount || 0) - 1));
        checkBookingStatus();
        refreshCalendar();

        setConfirmModal({
          isOpen: true,
          type: 'success',
          title: 'Buchung storniert',
          message: 'Ihre Buchung wurde erfolgreich storniert.',
          variant: 'single',
          onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
        });
      }
    } catch (error) {
      console.error('Error canceling event:', error);
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Stornierung fehlgeschlagen',
        message: 'Die Stornierung konnte nicht durchgefuehrt werden.',
        variant: 'single',
        onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = (response: any) => {
    onDelete(response);
    setShowDeleteModal(false);
    onClose();
  };

  const handleEventSelect = (selectedEvent: any) => {
    setEvent(selectedEvent);
    setShowEventList(false);
    onEventSelect(selectedEvent);
  };

  if (!event && events.length === 0) return null;

  // Event List View (Multiple events on same day)
  if (showEventList && events.length > 1) {
    return (
      <div
        className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
        onClick={onClose}
      >
        <div
          className="w-full sm:max-w-lg bg-white dark:bg-[#1c2432] border-t sm:border border-gray-200 dark:border-gray-700 rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col shadow-2xl animate-slide-up sm:animate-fade-in"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="w-9 h-9 rounded-xl bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 grid place-items-center flex-shrink-0">
              <CalendarIcon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">Termine</h3>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                {format(events[0]?.start || new Date(), 'EEEE, dd. MMMM yyyy', { locale: de })}
              </p>
            </div>
            <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 dark:text-gray-400 flex-shrink-0">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Event List */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            {events.map((evt) => (
              <div
                key={evt.id}
                className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600 hover:border-primary-500 dark:hover:border-primary-500 cursor-pointer transition-all"
                onClick={() => handleEventSelect(evt)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-1 h-12 rounded-full" style={{ backgroundColor: evt.extendedProps?.color || '#16a34a' }}></div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white">{evt.title}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {format(evt.start, 'HH:mm')} - {format(evt.end, 'HH:mm')} Uhr
                    </p>
                  </div>
                  <div className="text-xs px-2 py-1 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300">
                    {evt.extendedProps?.bookingCount || 0}/{evt.extendedProps?.bookable || 0}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!event) return null;

  // Calculate duration
  const durationMs = new Date(event.end).getTime() - new Date(event.start).getTime();
  const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
  const durationMinutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
  const durationText = durationHours > 0
    ? `${durationHours}${durationMinutes > 0 ? `,${durationMinutes}` : ''} Stunde${durationHours > 1 ? 'n' : ''} Dauer`
    : `${durationMinutes} Minuten Dauer`;

  // Generate capacity slots
  const renderCapacitySlots = () => {
    const slots = [];
    for (let i = 0; i < maxBookable; i++) {
      const isFilled = i < bookingCount;
      const isMySlot = isBooked && i === 0; // First slot is "mine" if booked

      let bgColor = 'bg-gray-200 dark:bg-gray-600';
      let extraClasses = '';

      if (isMySlot && isBooked) {
        bgColor = 'bg-success-500';
        extraClasses = 'ring-2 ring-success-300 dark:ring-success-600';
      } else if (isFilled) {
        bgColor = isFullyBooked ? 'bg-error-500' : 'bg-primary-500';
      }

      slots.push(
        <div key={i} className={`flex-1 h-2 ${bgColor} rounded-full ${extraClasses}`}></div>
      );
    }
    return slots;
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
        onClick={onClose}
      >
        <div
          className="w-full sm:max-w-lg bg-white dark:bg-[#1c2432] border-t sm:border border-gray-200 dark:border-gray-700 rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col shadow-2xl animate-slide-up sm:animate-fade-in"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <div
              className="w-9 h-9 rounded-xl grid place-items-center flex-shrink-0"
              style={{
                backgroundColor: isBooked ? 'rgb(220 252 231)' : isFullyBooked ? 'rgb(254 226 226)' : 'rgb(220 252 231)',
                color: getAccentColor()
              }}
            >
              {isBooked ? (
                <CheckCircleIcon className="h-5 w-5" />
              ) : (
                <CalendarIcon className="h-5 w-5" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                {events.length > 1 && (
                  <button onClick={() => setShowEventList(true)} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>
                )}
                <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">{getModalTitle()}</h3>
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">{getModalSubtitle()}</p>
            </div>
            <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 dark:text-gray-400 flex-shrink-0">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Body (scrollable) */}
          <div className="flex-1 overflow-y-auto">
          {/* Event Info Display */}
          <div className="px-5 py-3 sm:py-4 border-b border-gray-100 dark:border-gray-700">
            <h4 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white text-center mb-2 line-clamp-2">{event.title}</h4>
            {/* Mobile: stacked compact layout */}
            <div className="flex flex-col items-center gap-1 sm:hidden">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {format(event.start, 'EEEE, dd. MMMM', { locale: de })}
              </span>
              <span className="text-xl font-bold" style={{ color: getAccentColor() }}>
                {format(event.start, 'HH:mm')} - {format(event.end, 'HH:mm')} Uhr
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                <ClockIcon className="w-3.5 h-3.5" />
                {durationText}
              </span>
            </div>
            {/* Desktop: stacked layout */}
            <div className="hidden sm:flex sm:flex-col sm:items-center sm:gap-1">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {format(event.start, 'EEEE, dd. MMMM yyyy', { locale: de })}
              </p>
              <p className="text-xl font-bold" style={{ color: getAccentColor() }}>
                {format(event.start, 'HH:mm')} - {format(event.end, 'HH:mm')} Uhr
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                <ClockIcon className="w-3.5 h-3.5" />
                {durationText}
              </p>
            </div>
          </div>

          {/* Content Sections */}
          <div className="px-5 py-4 sm:py-5 space-y-4 sm:space-y-5">

            {/* Booking Details (if booked) */}
            {isBooked && bookingDetails.gebucht_am && (
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <CheckCircleIcon className="w-3.5 h-3.5" />
                  Buchungsdetails
                </h4>
                <div className="p-3 bg-success-50 dark:bg-success-900/20 rounded-xl border border-success-200 dark:border-success-700">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircleIcon className="w-4 h-4 text-success-600 dark:text-success-400" />
                    <span className="text-sm font-medium text-success-800 dark:text-success-300">Buchung bestaetigt</span>
                  </div>
                  <p className="text-xs text-success-700 dark:text-success-400">
                    Gebucht am {format(new Date(bookingDetails.gebucht_am), 'dd.MM.yyyy')} um {format(new Date(bookingDetails.gebucht_am), 'HH:mm')} Uhr
                  </p>
                </div>
              </div>
            )}

            {/* Trainer Section */}
            {event.extendedProps?.creator && (
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <UserIcon className="w-3.5 h-3.5" />
                  Trainer
                </h4>
                <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                  <ProfilePicture user={event.extendedProps.creator} size="xl" />
                  <div>
                    <p className="text-base font-medium text-gray-900 dark:text-white">{event.extendedProps.creator.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Trainer</p>
                  </div>
                </div>
              </div>
            )}

            {/* Co-Trainer Section */}
            {event.extendedProps?.cotrainer && (
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <UserIcon className="w-3.5 h-3.5" />
                  Co-Trainer
                </h4>
                <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                  <ProfilePicture user={event.extendedProps.cotrainer} size="xl" />
                  <div>
                    <p className="text-base font-medium text-gray-900 dark:text-white">{event.extendedProps.cotrainer.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Co-Trainer</p>
                  </div>
                </div>
              </div>
            )}

            {/* Capacity Section */}
            {maxBookable > 0 && (
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <UserGroupIcon className="w-3.5 h-3.5" />
                  Trainingsplätze
                </h4>
                <div className={`p-3 rounded-xl border ${isFullyBooked ? 'bg-error-50 dark:bg-error-900/20 border-error-200 dark:border-error-700' : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'}`}>
                  {/* Visual Slots */}
                  <div className="flex gap-1.5 mb-3">
                    {renderCapacitySlots()}
                  </div>
                  {/* Stats */}
                  <div className="flex items-center justify-center gap-4 text-sm">
                    {isBooked && (
                      <>
                        <div className="flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 bg-success-500 rounded-full"></div>
                          <span className="text-gray-600 dark:text-gray-300">Dein Platz</span>
                        </div>
                        <div className="w-px h-4 bg-gray-300 dark:bg-gray-600"></div>
                      </>
                    )}
                    <div className="flex items-center gap-1.5">
                      <div className={`w-2.5 h-2.5 ${isFullyBooked ? 'bg-error-500' : 'bg-primary-500'} rounded-full`}></div>
                      <span className={`${isFullyBooked ? 'text-error-700 dark:text-error-300 font-medium' : 'text-gray-600 dark:text-gray-300'}`}>
                        <strong className={isFullyBooked ? 'text-error-800 dark:text-error-200' : 'text-gray-900 dark:text-white'}>{bookingCount}</strong>
                        {isFullyBooked ? ` von ${maxBookable} Teilnehmer` : ` Teilnehmer`}
                      </span>
                    </div>
                    {!isFullyBooked && (
                      <>
                        <div className="w-px h-4 bg-gray-300 dark:bg-gray-600"></div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 bg-gray-200 dark:bg-gray-600 rounded-full border border-gray-300 dark:border-gray-500"></div>
                          <span className="text-gray-600 dark:text-gray-300">
                            <strong className="text-primary-600 dark:text-primary-400">{freeSlots}</strong> {freeSlots === 1 ? 'Platz frei' : 'Plätze frei'}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Warning Section (Issues) */}
            {myIssues && myIssues.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                  <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                  Hinweis
                </h4>
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-700">
                  <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">
                    {myIssues.length} Voraussetzung{myIssues.length > 1 ? 'en' : ''} nicht erfuellt
                  </p>
                  <ul className="mt-2 space-y-1 text-sm text-amber-700 dark:text-amber-400">
                    {myIssues.map((issue, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <XMarkIcon className="w-4 h-4 text-error-500 flex-shrink-0" />
                        {issue}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Info Section (if fully booked) */}
            {isFullyBooked && !isBooked && (
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                  Info
                </h4>
                <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Dieser Termin ist leider bereits ausgebucht. Schau dir andere Termine an oder wende dich an den Trainer.
                  </p>
                </div>
              </div>
            )}
          </div>
          </div>

          {/* Footer - Action Button */}
          {isVeranstaltung ? (
            <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700">
              {isBuchungsfristAbgelaufen && !canBookDespiteDeadline ? (
                <button
                  disabled
                  className="w-full py-3 bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 font-medium rounded-xl cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <ClockIcon className="w-5 h-5" />
                  Anmeldefrist abgelaufen
                </button>
              ) : (
                <button
                  onClick={() => { onClose(); navigate('/veranstaltungen'); }}
                  className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-xl shadow-lg shadow-purple-600/20 hover:shadow-purple-600/30 transition-all flex items-center justify-center gap-2"
                >
                  <TicketIcon className="w-5 h-5" />
                  Zur Veranstaltung
                </button>
              )}
            </div>
          ) : maxBookable > 0 && (
            <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700">
              {isBooked ? (
                // Stornieren Button
                <button
                  onClick={handleMainBook}
                  disabled={loading || (isBuchungsfristAbgelaufen && !canBookDespiteDeadline)}
                  className="w-full py-3 bg-error-600 hover:bg-error-700 text-white font-medium rounded-xl shadow-lg shadow-error-600/20 hover:shadow-error-600/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loading ? (
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <XMarkIcon className="w-5 h-5" />
                      Buchung stornieren
                    </>
                  )}
                </button>
              ) : isBuchungsfristAbgelaufen && !canBookDespiteDeadline ? (
                // Buchungsfrist abgelaufen (disabled)
                <button
                  disabled
                  className="w-full py-3 bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 font-medium rounded-xl cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <ClockIcon className="w-5 h-5" />
                  Anmeldefrist abgelaufen
                </button>
              ) : isFullyBooked ? (
                // Ausgebucht Button (disabled)
                <button
                  disabled
                  className="w-full py-3 bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 font-medium rounded-xl cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <XMarkIcon className="w-5 h-5" />
                  Ausgebucht
                </button>
              ) : (
                // Jetzt buchen Button
                <button
                  onClick={handleMainBook}
                  disabled={loading}
                  className="w-full py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-xl shadow-lg shadow-primary-600/20 hover:shadow-primary-600/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loading ? (
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <CheckCircleIcon className="w-5 h-5" />
                      Jetzt buchen
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {/* Options Section (Trainer only) */}
          {canManageEvent && (
            <div className="px-5 pb-5 border-t border-gray-200 dark:border-gray-700 pt-5">
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 text-center">
                Optionen
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {/* Teilnehmer */}
                <button
                  onClick={() => setShowParticipantsModal(true)}
                  className="flex items-center justify-center gap-2 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl border border-gray-200 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors"
                >
                  <UserGroupIcon className="w-5 h-5" />
                  Teilnehmer
                </button>

                {/* Gast */}
                <button
                  onClick={() => setShowGuestModal(true)}
                  disabled={isFullyBooked}
                  className={`flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                    isFullyBooked
                      ? 'bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      : 'bg-primary-100 dark:bg-primary-900/30 hover:bg-primary-200 dark:hover:bg-primary-900/50 border-primary-200 dark:border-primary-700 text-primary-700 dark:text-primary-300'
                  }`}
                >
                  <UserPlusIcon className="w-5 h-5" />
                  + Gast
                </button>

                {/* Bearbeiten */}
                {onEdit && (
                  <button
                    onClick={() => onEdit(event)}
                    className="flex items-center justify-center gap-2 py-2.5 bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 dark:hover:bg-amber-900/50 rounded-xl border border-amber-200 dark:border-amber-700 text-sm font-medium text-amber-700 dark:text-amber-300 transition-colors"
                  >
                    <PencilIcon className="w-5 h-5" />
                    Bearbeiten
                  </button>
                )}

                {/* Loeschen */}
                {!isPastEvent && (
                  <button
                    onClick={handleDelete}
                    className="flex items-center justify-center gap-2 py-2.5 bg-error-100 dark:bg-error-900/30 hover:bg-error-200 dark:hover:bg-error-900/50 rounded-xl border border-error-200 dark:border-error-700 text-sm font-medium text-error-700 dark:text-error-300 transition-colors"
                  >
                    <TrashIcon className="w-5 h-5" />
                    Loeschen
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ConfirmationModal */}
      <ConfirmationModal
        visible={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        variant={confirmModal.variant}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: confirmModal.onConfirm
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
          },
          confirm: {
            text: confirmModal.type === 'warning' && confirmModal.title.includes('stornieren') ? 'Stornieren' :
                   confirmModal.type === 'warning' ? 'Trotzdem buchen' : 'Bestaetigen',
            onPress: confirmModal.onConfirm
          }
        } : undefined}
      />

      {/* Guest Modal */}
      {showGuestModal && (
        <AddGuestModal
          event={event}
          isOpen={showGuestModal}
          onClose={() => setShowGuestModal(false)}
          onBook={() => {
            fetchParticipants();
            checkBookingStatus();
            refreshCalendar();
            setShowGuestModal(false);
          }}
          checkBooking={checkBookingStatus}
          refreshEvent={refreshCalendar}
        />
      )}

      {/* Participants Modal */}
      <ParticipantsModal
        event={event}
        isOpen={showParticipantsModal}
        onClose={() => setShowParticipantsModal(false)}
      />

      {/* Delete Event Modal */}
      <DeleteEventModal
        event={event}
        onDelete={handleDeleteConfirm}
        onClose={() => setShowDeleteModal(false)}
        user={user}
        isPastEvent={isPastEvent}
        isOpen={showDeleteModal}
      />
    </>
  );
};

export default EventModal;
